import { useContext, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { MODALS_ID } from '../../../constants'
import type { operations } from '../../../constants/schema'

import { checkNotificationPermission, toast } from '../../../utils'

import { StoresContext } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/ModalProvider'

import { useAlert } from '../../../hooks'
import { useUserInfo } from '../../../hooks/useUserInfo'

import { getTermsApproved, toggleTerms } from '../../../api/terms'

import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { ColumnItem } from '../../../components/common'
import { Text } from '../../../components/text'
import { Toggle } from '../../../components/toggle'
import { DivideLine } from '../../../components/divide'
import { Header } from '../../../components/header'
import { DeviceNotifyAllowModal } from './modals/DeviceNotifyAllowModal'

import { Color } from '../../../theme/color'
import { Spinner } from '../../../components/spinner/Spinner'

type PageName = operations['UserController_toggleCurrentUserTermsByPageName']['parameters']['path']['pageName']

export function Notification() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal } = useContext(ModalContext)

  const [approved, setApproved] = useState<{ email: boolean; sms: boolean; push: boolean }>({
    push: false,
    email: false,
    sms: false,
  })

  const openAlert = useAlert()
  const { data: userInfo, isLoading, isLogin, isCheckingLogin } = useUserInfo()

  useEffect(() => {
    setShowTab(false)
    loadTermsApproved()
    checkNotificationPermission()
  }, [])

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  const loadTermsApproved = async () => {
    const push = (await getTermsApproved('SIGNUP_MKT_PUSH')).result
    const sms = (await getTermsApproved('SIGNUP_MKT_SMS')).result
    const email = (await getTermsApproved('SIGNUP_MKT_EMAIL')).result

    setApproved({ sms, email, push })
  }

  if (!userInfo || isLoading) {
    return (
      <Wrap>
        <Header type="SUB" title="알림 설정" />
        <Spinner />
      </Wrap>
    )
  }

  const pushEventListener = (e: any) => {
    document.removeEventListener('checkDeviceNotification', pushEventListener)
    if (e.detail === 'denied') {
      openModal({ id: MODALS_ID.DEVICE_NOTIFY_ALLOW, Component: DeviceNotifyAllowModal })
      return
    }
    togglePushChecked()
  }

  const handlePushToggle = () => {
    if (approved.push) {
      togglePushChecked()
      return
    }

    document.addEventListener('checkDeviceNotification', pushEventListener)
    checkNotificationPermission()
  }

  const togglePushChecked = async () => {
    try {
      const { result } = await toggleTerms({ pageName: 'SIGNUP_MKT_PUSH' })
      marketingAlert(result)
      await loadTermsApproved()
    } catch (error) {
      toast('알림 상태 변경을 실패했어요.', error)
    }
  }

  const toggleMarketingChecked = async (pageName: PageName) => {
    try {
      // 서버에 약관 pageName에 맞는 알림 동의/거부 설정 요청
      const { result } = await toggleTerms({ pageName })

      // 이전 동의 정보
      const { email, sms, push } = approved

      // 이전 마케팅 정보 알림이 모두 꺼져 있는데 email/sms/push 중 하나라도 켰을 때
      const isOnlyOneToggleOn = !email && !sms && !push && result

      // email/sms/push 중 한 개만 켜져 있는데 나머지 하나도 껐을 때
      const isAllToggleOff =
        (email && !sms && !push && !result) ||
        (!email && sms && !push && !result) ||
        (!email && !sms && push && !result)

      if (isOnlyOneToggleOn || isAllToggleOff) {
        marketingAlert(result)
      }

      // 동의 정보 동기화
      await loadTermsApproved()
    } catch (error) {
      toast('알림 상태 변경을 실패했어요.', error)
    }
  }

  const marketingAlert = (result: boolean) => {
    const userName = userInfo.identification?.danalName ?? userInfo.name
    openAlert({
      content: (
        <ColumnItem $gap={16}>
          <Text size={18} weight={600} $textAlign="center">
            마케팅 정보 알림 수신 {result ? '동의' : '거부'} 처리 결과
          </Text>
          <Text size={14} weight={500} color="text_secondary" lineHeight={20} $textAlign="center">
            {userName ? (
              <>
                전송자 : {userName}
                <br />
              </>
            ) : null}
            일시 : {dayjs().format('YYYY.MM.DD')}
            <br />
            내용 : 수신 {result ? '동의' : '거부'} 처리 완료
          </Text>
        </ColumnItem>
      ),
    })
  }

  return (
    <Wrap>
      <Header type="SUB" title="알림 설정" />
      <Content backgroundColor="gray_1">
        <Container>
          <List>
            <Item>
              <TitleBox>
                <Text size={18} weight={500}>
                  서비스 이용 알림
                </Text>
                <Text size={12} color="text_secondary">
                  자산 변동 및 공지, 작품 소식 등 주요 정보에 대한 알림
                </Text>
              </TitleBox>
              <Toggle defaultChecked={true} disabled={true} />
            </Item>
          </List>
          <DivideLine />
          <List>
            <Item>
              <TitleBox>
                <Text size={18} weight={500}>
                  마케팅 정보 알림
                </Text>
                <Text size={12} color="text_secondary">
                  이벤트/프로모션 등 혜택에 대한 알림
                </Text>
              </TitleBox>
            </Item>
            <SubItem>
              <TitleBox>
                <Text size={16} weight={500}>
                  이메일
                </Text>
              </TitleBox>
              <Toggle
                defaultChecked={approved.email}
                checked={approved.email}
                onToggle={() => toggleMarketingChecked('SIGNUP_MKT_EMAIL')}
              />
            </SubItem>
            <SubItem>
              <TitleBox>
                <Text size={16} weight={500}>
                  문자(SMS)
                </Text>
              </TitleBox>
              <Toggle
                defaultChecked={approved.sms}
                checked={approved.sms}
                onToggle={() => toggleMarketingChecked('SIGNUP_MKT_SMS')}
              />
            </SubItem>
            <SubItem>
              <TitleBox>
                <Text size={16} weight={500}>
                  앱 푸시 알림
                </Text>
              </TitleBox>
              <Toggle defaultChecked={approved.push} checked={approved.push} onToggle={handlePushToggle} />
            </SubItem>
          </List>
        </Container>
        <NoticeContainer>
          <Text size={14} lineHeight={22} color="text_secondary" block={true}>
            ※마케팅 정보 알림 동의와 함께 마케팅 목적의 개인정보 수집 및 이용에 동의하시게 됩니다.
            <br />
            <br />
            •수집 목적 :  마케팅 정보 수신
            <br />
            •수집 항목 :  휴대폰 번호, 이메일
            <br />
            •보유 및 이용기간 :  수신 동의 철회 시 또는 회원 탈퇴 시까지
            <br />
            •동의 거부 시에도 아티피오 서비스 이용에 제한은 없어요.
            <br />
            <br />
          </Text>
          <Text size={14} lineHeight={22} color="error" block={true}>
            ※꼭 알아야 하는 중요한 정보는 동의와 상관없이 알려드려요.
          </Text>
        </NoticeContainer>
      </Content>
    </Wrap>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background-color: ${Color.white};
`

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Item = styled.li`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
`

const SubItem = styled(Item)`
  padding-left: 24px;
`

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const NoticeContainer = styled.div`
  padding: 24px;
  background-color: ${Color.gray_1};
`
